/* eslint-disable @typescript-eslint/no-explicit-any -- API service returns untyped JSON responses */
/**
 * Vector Exports API Service
 *
 * Handles all vector export-related API calls for S3 Vectors integration
 */

import { AI_OPERATIONS_API_URL } from './config';
import { demoModeManager } from './demoModeManager';
import { getAIOpsMockData } from './mockData';

const API_BASE_URL = `${AI_OPERATIONS_API_URL}/genaiops`;

const isDemoActive = (): boolean => demoModeManager.getDemoMode().isDemoMode;

/**
 * Get the selected client ID from localStorage
 * Used by partner/aws/admin users who need to specify which client they're viewing
 */
const getSelectedClientId = (): string | null => {
  return localStorage.getItem('selectedClientId');
};

const getSelectedProjectId = (): string | null => {
  return localStorage.getItem('selectedProjectId');
};

/**
 * Append client_id to URL if available (for partner/aws/admin users)
 */
const appendClientId = (url: string): string => {
  const clientId = getSelectedClientId();
  if (!clientId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}client_id=${clientId}`;
};

const appendProjectId = (url: string): string => {
  try {
    const stored = localStorage.getItem('globalFilter');
    if (stored) {
      const { filterType, selectedId } = JSON.parse(stored);
      if (selectedId) {
        const separator = url.includes('?') ? '&' : '?';
        if (filterType === 'project') return `${url}${separator}project_id=${selectedId}`;
        if (filterType === 'workload') return `${url}${separator}workload_id=${selectedId}`;
        if (filterType === 'account') return `${url}${separator}account_id=${selectedId}`;
        if (filterType === 'environment') return `${url}${separator}environment_id=${selectedId}`;
      }
    }
  } catch { /* ignore */ }
  const projectId = getSelectedProjectId();
  if (!projectId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}project_id=${projectId}`;
};

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

export interface ListVectorExportsParams {
  page?: number;
  limit?: number;
  dataset_id?: string | null;
  project_id?: string | null;
  status?: string | null;
}

export interface VectorExportData {
  dataset_id: string;
  aws_account_id: string;
  display_name: string;
  description?: string;
  region?: string;
  auto_sync?: boolean;
  embedding_source?: string;
  [key: string]: any;
}

/**
 * List all vector exports with pagination and filtering
 */
export const listVectorExports = async (params: ListVectorExportsParams = {}): Promise<any> => {
  if (isDemoActive()) return getAIOpsMockData(appendProjectId(appendClientId('/genaiops/vector-exports')));

  const {
    page = 1,
    limit = 50,
    dataset_id = null,
    project_id = null,
    status = null
  } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(dataset_id && { dataset_id }),
    ...(project_id && { project_id }),
    ...(status && { status })
  });

  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports?${queryParams}`)), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch vector exports: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a single vector export by ID
 */
export const getVectorExport = async (exportId: string): Promise<any> => {
  if (isDemoActive()) return getAIOpsMockData(appendProjectId(appendClientId(`/genaiops/vector-exports/${exportId}`)));

  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}`)), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch vector export: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new vector export
 */
export const createVectorExport = async (exportData: VectorExportData): Promise<any> => {
  if (isDemoActive()) {
    return {
      id: 'demo-ve-' + Date.now(),
      ...exportData,
      status: 'pending',
      vector_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }

  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports`)), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(exportData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to create vector export: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Sync a vector export with its source dataset
 * Re-generates embeddings for new/changed items
 */
export const syncVectorExport = async (exportId: string): Promise<any> => {
  if (isDemoActive()) return { success: true, message: 'Sync started' };

  const response = await fetch(appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}/sync`)), {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to sync vector export: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Delete a vector export
 *
 * @param exportId - UUID of the export to delete
 * @param deleteAwsResources - Also delete bucket/index in client's AWS account
 */
export const deleteVectorExport = async (exportId: string, deleteAwsResources: boolean = false): Promise<any> => {
  if (isDemoActive()) return { success: true };

  const queryParams = new URLSearchParams({
    delete_aws_resources: deleteAwsResources.toString()
  });

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}?${queryParams}`)),
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to delete vector export: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get usage example code for a vector export
 * Returns Python code, CLI commands, and Bedrock KB integration instructions
 */
export const getVectorExportUsageExample = async (exportId: string): Promise<any> => {
  if (isDemoActive()) return getAIOpsMockData(`/genaiops/vector-exports/${exportId}/usage-example`);

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}/usage-example`)),
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch usage example: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get vector exports for a specific dataset
 * Shortcut for filtering by dataset_id
 */
export const getDatasetVectorExports = async (datasetId: string): Promise<any> => {
  if (isDemoActive()) return getAIOpsMockData(appendProjectId(appendClientId(`/genaiops/datasets/${datasetId}/vector-exports`)));

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/datasets/${datasetId}/vector-exports`)),
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch dataset vector exports: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Quick export a dataset to S3 Vectors
 * Shortcut endpoint on the dataset resource
 *
 * @param datasetId - UUID of the dataset to export
 * @param exportData - Export configuration (same as createVectorExport)
 */
export const exportDatasetToVectors = async (datasetId: string, exportData: Record<string, any>): Promise<any> => {
  if (isDemoActive()) {
    return {
      id: 'demo-ve-' + Date.now(),
      dataset_id: datasetId,
      ...exportData,
      status: 'pending',
      vector_count: 0,
      created_at: new Date().toISOString(),
    };
  }

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/datasets/${datasetId}/export-to-vectors`)),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(exportData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to export dataset: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get export status (for polling during long exports)
 */
export const getVectorExportStatus = async (exportId: string): Promise<any> => {
  if (isDemoActive()) {
    const data: any = getAIOpsMockData(`/genaiops/vector-exports/${exportId}`);
    return {
      status: data.status,
      vector_count: data.vector_count,
      error_message: data.error_message || null,
      last_sync_at: data.last_sync_at
    };
  }

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}`)),
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch export status: ${response.statusText}`);
  }

  const data = await response.json();
  return {
    status: data.status,
    vector_count: data.vector_count,
    error_message: data.error_message,
    last_sync_at: data.last_sync_at
  };
};

export const assignVectorExportProject = async (exportId: string, projectId: string | null): Promise<any> => {
  if (isDemoActive()) return { id: exportId, project_id: projectId };

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}/project`)),
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ project_id: projectId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to assign vector export project: ${response.statusText}`);
  }

  return response.json();
};

export const bulkAssignVectorExportProject = async (ids: string[], projectId: string | null): Promise<any> => {
  if (isDemoActive()) return { updated: ids.length, project_id: projectId };

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/bulk-assign-project`)),
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids, project_id: projectId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to bulk assign vector export project: ${response.statusText}`);
  }

  return response.json();
};

export const assignVectorExportWorkload = async (exportId: string, workloadId: string | null): Promise<any> => {
  if (isDemoActive()) return { id: exportId, workload_id: workloadId };

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/${exportId}/workload`)),
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ workload_id: workloadId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to assign vector export workload: ${response.statusText}`);
  }

  return response.json();
};

export const bulkAssignVectorExportWorkload = async (ids: string[], workloadId: string | null): Promise<any> => {
  if (isDemoActive()) return { updated: ids.length, workload_id: workloadId };

  const response = await fetch(
    appendProjectId(appendClientId(`${API_BASE_URL}/vector-exports/bulk-assign-workload`)),
    {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids, workload_id: workloadId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to bulk assign vector export workload: ${response.statusText}`);
  }

  return response.json();
};
